import { combineEpics, Epic, ofType } from 'redux-observable'
import {
  catchError,
  EMPTY,
  filter,
  mergeMap,
  of,
  switchMap,
  withLatestFrom
} from 'rxjs'
import {
  isMainOpenFinWindow,
  isOpenFinRuntime
} from '../../containers/OpenFin/helpers'
import { createInvokeEpic } from '../../signalr/createInvokeEpic'
import { createStreamEpic } from '../../signalr/createStreamEpic'
import { querySearch$ } from '../searchSecurities/epic'
import { addToast } from '../toasts/actions'
import { Toast } from '../toasts/types'
import { logError } from '../ws/actions'
import {
  PopoutSecurityAction,
  setDomLimitExceeded,
  SetDomSecurityByQueryAction,
  updatePopouts
} from './actions'
import {
  forceCancelDefinition,
  markAsRestrictedDefinition,
  markAsUnrestrictedDefinition,
  subscribeDepthForSecurityDefinition
} from './definition'
import { State } from './reducer'
import { getDOMLimitExceeded, getMaxDOMs } from './selectors'

type DepthSliceState = { depthofmarket: State }

const setDomSecurityByQuery: Epic = (action$) =>
  action$.pipe(
    ofType<SetDomSecurityByQueryAction>('depthofmarket.setDomSecurityByQuery'),
    switchMap((action) => {
      const { query, oldSecurityId } = action.payload
      const search$ = querySearch$(query)
      return search$.pipe(
        mergeMap((securities) =>
          securities.length
            ? of(updatePopouts(oldSecurityId, securities[0].id))
            : EMPTY
        ),
        catchError((err) => of(logError(err)))
      )
    })
  )

const domExceededEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<PopoutSecurityAction>('depthofmarket.popout'),
    withLatestFrom(state$),
    filter(
      ([, state]: [unknown, DepthSliceState]) =>
        getDOMLimitExceeded(state) &&
        (!isOpenFinRuntime() || isMainOpenFinWindow())
    ),
    mergeMap(([, state]: [unknown, DepthSliceState]) => {
      const maxDOMs = getMaxDOMs(state)
      const contents: Toast = {
        message: `Maximum amount of depth windows (${maxDOMs}) have been opened. Please close some windows before opening more.`,
        options: {
          toastId: `domlimit`,
          type: 'warning',
          onClose: () => {
            setDomLimitExceeded(false)
          }
        }
      }
      return of(addToast(contents))
    })
  )

export default combineEpics(
  createStreamEpic(
    'depthofmarket.subscribeDepthForSecurity',
    subscribeDepthForSecurityDefinition
  ),
  createInvokeEpic(
    'depthofmarket.markAsRestricted',
    markAsRestrictedDefinition
  ),
  createInvokeEpic(
    'depthofmarket.markAsUnrestricted',
    markAsUnrestrictedDefinition
  ),
  createInvokeEpic('depthofmarket.forceCancel', forceCancelDefinition),
  setDomSecurityByQuery,
  domExceededEpic
)
